import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SelectedCountry {
  id: string;
  code: string;
  name: string;
}

interface CountryContextValue {
  selectedCountry: SelectedCountry | null;
  setSelectedCountry: (c: SelectedCountry) => void;
  allCountries: SelectedCountry[];
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'ww_country_v2'; // bumped to avoid conflict with old key

function loadFromStorage(): SelectedCountry | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SelectedCountry) : null;
  } catch {
    return null;
  }
}

function saveToStorage(c: SelectedCountry) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
}

// ─── Context ──────────────────────────────────────────────────────────────────

const CountryContext = createContext<CountryContextValue>({
  selectedCountry: null,
  setSelectedCountry: () => {},
  allCountries: [],
});

export const useCountry = () => useContext(CountryContext);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const CountryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [allCountries, setAllCountries] = useState<SelectedCountry[]>([]);
  const [selectedCountry, setSelectedCountryState] = useState<SelectedCountry | null>(
    loadFromStorage,
  );

  // Fetch countries once on mount; auto-select first if nothing stored
  useEffect(() => {
    api.get('/locations/countries')
      .then((res) => {
        const data: SelectedCountry[] = (res.data?.data ?? []).map(
          (c: { id: string; code: string; name: string }) => ({
            id: c.id,
            code: c.code,
            name: c.name,
          }),
        );
        setAllCountries(data);

        if (!loadFromStorage() && data.length > 0) {
          saveToStorage(data[0]);
          setSelectedCountryState(data[0]);
        }
      })
      .catch(() => {});
  }, []);

  const setSelectedCountry = useCallback((c: SelectedCountry) => {
    saveToStorage(c);
    setSelectedCountryState(c);
  }, []);

  return (
    <CountryContext.Provider value={{ selectedCountry, setSelectedCountry, allCountries }}>
      {children}
    </CountryContext.Provider>
  );
};
